import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { Toast } from "primereact/toast";

import { translationKeys } from "../../../../models/app-translation-keys";
import { useStore } from "../../../../stores/StoreContext";

export interface IFileGroundingLimits {
  allowedFileTypes: string[];
  allowedFileSize: number;
  allowedTotalFileSize: number;
}

/**
 * Shared validation logic for adding grounding files to the chatStore, so both the manual
 * FileSelector and the drag & drop FileDropZone behave identically (same rules, same error toasts).
 */
export const useFileGroundingValidation = ({
  allowedFileTypes,
  allowedFileSize,
  allowedTotalFileSize,
}: IFileGroundingLimits) => {
  const { t } = useTranslation();
  const { chatStore } = useStore();

  const fileSelectorKeys = translationKeys.feature.chatbot.fileGroundings;

  const errorToastRef = useRef<Toast>(null);

  const toMegaBytes = (bytes: number) => bytes / (1024 * 1024);

  const addFiles = (files: FileList | File[]) => {
    const validFiles = Array.from(files).reduce<File[]>((validFiles: File[], file: File) => {
      const isValidFileExtension = allowedFileTypes.some(ext => file.name.toLowerCase().endsWith(ext));

      const isValidFileSize = file.size <= allowedFileSize;

      if (isValidFileExtension && isValidFileSize) {
        validFiles.push(file);
      } else {
        if (!isValidFileExtension) {
          errorToastRef.current?.show({
            severity: "error",
            summary: t(fileSelectorKeys.invalidFile),
            detail: t(fileSelectorKeys.invalidFileType, { fileName: file.name }),
          });
        }
        if (!isValidFileSize) {
          errorToastRef.current?.show({
            severity: "error",
            summary: t(fileSelectorKeys.invalidFile),
            detail: t(fileSelectorKeys.fileTooLarge, {
              maxFileSize: toMegaBytes(allowedFileSize).toFixed(2),
              fileName: file.name,
            }),
          });
        }
      }

      return validFiles;
    }, []);

    const result = chatStore.tryAddSelectedFiles(validFiles, allowedTotalFileSize);
    if (!result) {
      errorToastRef.current?.show({
        severity: "error",
        summary: t(fileSelectorKeys.invalidFile),
        detail: t(fileSelectorKeys.totalFileSizeExceeded, {
          maxTotalFileSize: toMegaBytes(allowedTotalFileSize).toFixed(2),
        }),
      });
    }
  };

  return { addFiles, errorToastRef };
};
